import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useContent } from "@/contexts/ContentContext";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    LogOut,
    Inbox,
    Image as ImageIcon,
    Settings,
    Star,
    Trash2,
    Edit3,
    Plus,
    Save,
    Upload,
    ExternalLink,
} from "lucide-react";

const TABS = [
    { id: "leads", label: "Leads", icon: Inbox },
    { id: "content", label: "Site Content", icon: Settings },
    { id: "services", label: "Services", icon: Star },
    { id: "gallery", label: "Gallery", icon: ImageIcon },
    { id: "testimonials", label: "Reviews", icon: Star },
];

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const { content, services, gallery, testimonials, refresh } = useContent();
    const navigate = useNavigate();
    const [tab, setTab] = useState("leads");

    const doLogout = async () => {
        await logout();
        navigate("/admin/login");
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
            {/* Top bar */}
            <header className="border-b border-zinc-900 bg-black sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="font-display text-2xl">
                            Ruiz<span className="text-yellow-400">.</span>Admin
                        </div>
                        <span className="font-mono-accent text-[10px] text-zinc-600 hidden md:inline">
                            {user?.email}
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            target="_blank"
                            className="font-mono-accent text-[10px] text-zinc-400 hover:text-yellow-400 inline-flex items-center gap-2"
                            data-testid="admin-view-site"
                        >
                            VIEW SITE <ExternalLink className="w-3 h-3" />
                        </Link>
                        <button
                            onClick={doLogout}
                            data-testid="admin-logout"
                            className="font-mono-accent text-[10px] text-zinc-400 hover:text-yellow-400 inline-flex items-center gap-2"
                        >
                            LOGOUT <LogOut className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
                {/* Tabs */}
                <div className="flex flex-wrap gap-2 border-b border-zinc-900 mb-8">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                data-testid={`tab-${t.id}`}
                                className={`px-5 py-3 font-mono-accent text-[11px] flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                                    tab === t.id
                                        ? "border-yellow-400 text-yellow-400"
                                        : "border-transparent text-zinc-500 hover:text-white"
                                }`}
                            >
                                <Icon className="w-3.5 h-3.5" /> {t.label}
                            </button>
                        );
                    })}
                </div>

                {tab === "leads" && <LeadsPanel />}
                {tab === "content" && content && <ContentPanel content={content} onSaved={refresh} />}
                {tab === "services" && <ServicesPanel items={services} onChange={refresh} />}
                {tab === "gallery" && <GalleryPanel items={gallery} onChange={refresh} />}
                {tab === "testimonials" && (
                    <TestimonialsPanel items={testimonials} onChange={refresh} />
                )}
            </div>
        </div>
    );
}

// ---------------- LEADS ----------------
function LeadsPanel() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/leads");
            setLeads(data);
        } catch {
            toast.error("Failed to load leads");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const del = async (id) => {
        if (!window.confirm("Delete this lead?")) return;
        await api.delete(`/leads/${id}`);
        setLeads(leads.filter((l) => l.id !== id));
        toast.success("Lead deleted");
    };

    const markRead = async (id) => {
        await api.patch(`/leads/${id}/read`);
        setLeads(leads.map((l) => (l.id === id ? { ...l, read: true } : l)));
    };

    if (loading) return <div className="font-mono-accent text-xs text-zinc-500">Loading leads…</div>;

    if (leads.length === 0)
        return (
            <div className="border border-dashed border-zinc-800 p-16 text-center">
                <Inbox className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
                <div className="font-display text-3xl text-white">No leads yet.</div>
                <p className="text-zinc-500 mt-2 text-sm">
                    Quote requests submitted on the site will appear here.
                </p>
            </div>
        );

    return (
        <div className="space-y-3" data-testid="leads-list">
            {leads.map((l) => (
                <div
                    key={l.id}
                    data-testid={`lead-${l.id}`}
                    className={`border p-5 md:p-6 grid md:grid-cols-12 gap-4 ${
                        l.read ? "border-zinc-900 bg-black" : "border-yellow-400/30 bg-yellow-400/[0.03]"
                    }`}
                >
                    <div className="md:col-span-3">
                        <div className="font-mono-accent text-[9px] text-zinc-600 mb-1">
                            {new Date(l.created_at).toLocaleString()}
                        </div>
                        <div className="font-display text-2xl text-white">{l.name}</div>
                        <a href={`tel:${l.phone}`} className="text-yellow-400 text-sm hover:underline">
                            {l.phone}
                        </a>
                        {l.email ? (
                            <a
                                href={`mailto:${l.email}`}
                                className="text-zinc-400 text-sm block hover:text-yellow-400 break-all"
                            >
                                {l.email}
                            </a>
                        ) : null}
                    </div>
                    <div className="md:col-span-3 text-sm">
                        <div className="font-mono-accent text-[9px] text-zinc-600">SERVICE</div>
                        <div className="text-white">{l.service || "—"}</div>
                        <div className="font-mono-accent text-[9px] text-zinc-600 mt-3">ADDRESS</div>
                        <div className="text-zinc-300">{l.address || "—"}</div>
                    </div>
                    <div className="md:col-span-4 text-sm">
                        <div className="font-mono-accent text-[9px] text-zinc-600">MESSAGE</div>
                        <p className="text-zinc-300 whitespace-pre-wrap">{l.message || "—"}</p>
                    </div>
                    <div className="md:col-span-2 flex md:flex-col gap-2 md:items-end">
                        {!l.read && (
                            <button
                                onClick={() => markRead(l.id)}
                                className="border border-zinc-700 hover:border-yellow-400 hover:text-yellow-400 px-3 py-2 font-mono-accent text-[10px]"
                                data-testid={`lead-read-${l.id}`}
                            >
                                MARK READ
                            </button>
                        )}
                        <button
                            onClick={() => del(l.id)}
                            className="border border-zinc-800 hover:border-red-500 hover:text-red-500 px-3 py-2 font-mono-accent text-[10px] inline-flex items-center gap-1"
                            data-testid={`lead-delete-${l.id}`}
                        >
                            <Trash2 className="w-3 h-3" /> DELETE
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ---------------- CONTENT ----------------
function ContentPanel({ content, onSaved }) {
    const [hero, setHero] = useState(content.hero);
    const [business, setBusiness] = useState(content.business);
    const [contact, setContact] = useState(content.contact);
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/content", { hero, business, contact });
            await onSaved();
            toast.success("Content saved");
        } catch (e) {
            toast.error("Failed to save");
        } finally {
            setBusy(false);
        }
    };

    const onLogoFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 3 * 1024 * 1024) {
            toast.error("Please upload an image under 3MB");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setHero({ ...hero, logo_url: reader.result });
            toast.success("Logo uploaded. Click Save to apply.");
        };
        reader.readAsDataURL(f);
    };

    return (
        <div className="space-y-10" data-testid="content-panel">
            {/* HERO */}
            <div className="border border-zinc-900 bg-[#0a0a0a] p-6 md:p-8">
                <div className="font-display text-3xl mb-6">
                    Hero <span className="text-yellow-400">Section</span>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                    <Input label="EYEBROW" value={hero.eyebrow} onChange={(v) => setHero({ ...hero, eyebrow: v })} />
                    <div className="md:col-span-1">
                        <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">LOGO</label>
                        <div className="flex items-center gap-4">
                            <img
                                src={hero.logo_url}
                                alt=""
                                className="w-16 h-16 object-cover border border-zinc-800"
                            />
                            <label className="border border-zinc-700 hover:border-yellow-400 hover:text-yellow-400 px-4 py-3 font-mono-accent text-[10px] cursor-pointer inline-flex items-center gap-2">
                                <Upload className="w-3 h-3" /> UPLOAD
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={onLogoFile}
                                    className="hidden"
                                    data-testid="hero-logo-upload"
                                />
                            </label>
                        </div>
                    </div>
                    <Input label="HEADLINE TOP" value={hero.headline_top} onChange={(v) => setHero({ ...hero, headline_top: v })} />
                    <Input label="HEADLINE ACCENT" value={hero.headline_accent} onChange={(v) => setHero({ ...hero, headline_accent: v })} />
                    <div className="md:col-span-2">
                        <TextArea label="SUBHEADLINE" value={hero.subheadline} onChange={(v) => setHero({ ...hero, subheadline: v })} />
                    </div>
                    <Input label="PRIMARY CTA" value={hero.cta_primary} onChange={(v) => setHero({ ...hero, cta_primary: v })} />
                    <Input label="SECONDARY CTA" value={hero.cta_secondary} onChange={(v) => setHero({ ...hero, cta_secondary: v })} />
                    <div className="md:col-span-2">
                        <Input
                            label="BACKGROUND IMAGE URL"
                            value={hero.background_url}
                            onChange={(v) => setHero({ ...hero, background_url: v })}
                        />
                    </div>
                    {/* Stats */}
                    <div className="md:col-span-2">
                        <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">
                            STATS BAR
                        </label>
                        <div className="grid md:grid-cols-3 gap-3">
                            {(hero.stats || []).map((s, i) => (
                                <div key={i} className="border border-zinc-800 p-3 space-y-2">
                                    <input
                                        value={s.value}
                                        onChange={(e) => {
                                            const arr = [...hero.stats];
                                            arr[i] = { ...arr[i], value: e.target.value };
                                            setHero({ ...hero, stats: arr });
                                        }}
                                        placeholder="Value"
                                        className="w-full bg-black border border-zinc-800 text-white px-3 py-2 font-display text-2xl text-yellow-400"
                                    />
                                    <input
                                        value={s.label}
                                        onChange={(e) => {
                                            const arr = [...hero.stats];
                                            arr[i] = { ...arr[i], label: e.target.value };
                                            setHero({ ...hero, stats: arr });
                                        }}
                                        placeholder="Label"
                                        className="w-full bg-black border border-zinc-800 text-white px-3 py-2 text-xs"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* BUSINESS */}
            <div className="border border-zinc-900 bg-[#0a0a0a] p-6 md:p-8">
                <div className="font-display text-3xl mb-6">
                    Business <span className="text-yellow-400">Info</span>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                    <Input label="NAME" value={business.name} onChange={(v) => setBusiness({ ...business, name: v })} />
                    <Input label="TAGLINE" value={business.tagline} onChange={(v) => setBusiness({ ...business, tagline: v })} />
                    <div className="md:col-span-2">
                        <Input label="ABOUT TITLE" value={business.about_title} onChange={(v) => setBusiness({ ...business, about_title: v })} />
                    </div>
                    <div className="md:col-span-2">
                        <TextArea label="ABOUT BODY" value={business.about_body} onChange={(v) => setBusiness({ ...business, about_body: v })} />
                    </div>
                    <div className="md:col-span-2">
                        <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">
                            WHY US (one per line)
                        </label>
                        <textarea
                            value={(business.why_us || []).join("\n")}
                            onChange={(e) =>
                                setBusiness({ ...business, why_us: e.target.value.split("\n").filter(Boolean) })
                            }
                            rows={6}
                            className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none text-white px-4 py-3 resize-none"
                        />
                    </div>
                </div>
            </div>

            {/* CONTACT */}
            <div className="border border-zinc-900 bg-[#0a0a0a] p-6 md:p-8">
                <div className="font-display text-3xl mb-6">
                    Contact <span className="text-yellow-400">Info</span>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                    <Input label="PHONE" value={contact.phone} onChange={(v) => setContact({ ...contact, phone: v })} />
                    <Input label="EMAIL" value={contact.email} onChange={(v) => setContact({ ...contact, email: v })} />
                    <Input label="ADDRESS" value={contact.address} onChange={(v) => setContact({ ...contact, address: v })} />
                    <Input label="SERVICE AREA" value={contact.service_area} onChange={(v) => setContact({ ...contact, service_area: v })} />
                    <Input label="HOURS" value={contact.hours} onChange={(v) => setContact({ ...contact, hours: v })} />
                </div>
            </div>

            <div className="sticky bottom-4 flex justify-end">
                <button
                    onClick={save}
                    disabled={busy}
                    data-testid="content-save"
                    className="bg-yellow-400 text-black font-black uppercase tracking-wider px-8 py-4 text-sm hover:bg-yellow-300 disabled:opacity-60 inline-flex items-center gap-3 shadow-lg shadow-black/60"
                >
                    <Save className="w-4 h-4" /> {busy ? "SAVING…" : "SAVE CHANGES"}
                </button>
            </div>
        </div>
    );
}

// ---------------- SERVICES ----------------
const emptyService = { title: "", tagline: "", description: "", icon: "hammer", order: 0, image_url: "", image_urls: [] };

function ServicesPanel({ items, onChange }) {
    const [editing, setEditing] = useState(null);

    const startNew = () => setEditing({ ...emptyService, __new: true });

    const save = async (form) => {
        try {
            const clean = { ...form };
            clean.image_urls = (clean.image_urls || []).filter(Boolean).slice(0, 3);
            clean.image_url = clean.image_urls[0] || clean.image_url || "";
            if (form.__new) {
                const { __new, id, ...body } = clean;
                await api.post("/services", body);
                toast.success("Service added");
            } else {
                const { id, ...body } = clean;
                await api.put(`/services/${id}`, body);
                toast.success("Service updated");
            }
            setEditing(null);
            await onChange();
        } catch {
            toast.error("Save failed");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this service?")) return;
        await api.delete(`/services/${id}`);
        await onChange();
        toast.success("Service deleted");
    };

    return (
        <div data-testid="services-panel">
            <div className="flex items-center justify-between mb-6">
                <div className="font-display text-3xl">
                    Services <span className="text-yellow-400">({items.length})</span>
                </div>
                <button
                    onClick={startNew}
                    data-testid="add-service"
                    className="bg-yellow-400 text-black font-black uppercase tracking-wider px-4 py-3 text-xs hover:bg-yellow-300 inline-flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" /> ADD SERVICE
                </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {items.map((s) => {
                    const photos = (s.image_urls && s.image_urls.length ? s.image_urls : s.image_url ? [s.image_url] : []).slice(0, 3);
                    return (
                        <div key={s.id} className="border border-zinc-900 bg-[#0a0a0a] flex gap-4 overflow-hidden">
                            <div className="w-32 md:w-40 shrink-0 grid grid-rows-3 gap-px bg-zinc-900 border-r border-zinc-900">
                                {[0, 1, 2].map((i) => (
                                    <div key={i} className="bg-zinc-950 relative">
                                        {photos[i] ? (
                                            <img
                                                src={photos[i]}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center font-mono-accent text-[9px] text-zinc-600">
                                                #{i + 1}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <div className="flex-1 p-4 flex justify-between items-start gap-3 min-h-[130px]">
                                <div className="min-w-0">
                                    <div className="font-mono-accent text-[10px] text-yellow-400">
                                        #{s.order} · {s.icon} · {photos.length}/3 PHOTOS
                                    </div>
                                    <div className="font-display text-xl md:text-2xl text-white mt-1">
                                        {s.title}
                                    </div>
                                    <div className="text-zinc-500 text-xs mt-2">{s.tagline}</div>
                                </div>
                                <div className="flex flex-col gap-2 shrink-0">
                                    <button
                                        onClick={() => setEditing(s)}
                                        className="border border-zinc-700 hover:border-yellow-400 hover:text-yellow-400 p-2"
                                        data-testid={`edit-service-${s.id}`}
                                    >
                                        <Edit3 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => del(s.id)}
                                        className="border border-zinc-800 hover:border-red-500 hover:text-red-500 p-2"
                                        data-testid={`delete-service-${s.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {editing && (
                <ServiceEditor
                    form={editing}
                    onCancel={() => setEditing(null)}
                    onSave={save}
                />
            )}
        </div>
    );
}

function ServiceEditor({ form: initial, onCancel, onSave }) {
    // Normalize to 3-slot array for consistent editing
    const init3 = () => {
        const arr = (initial.image_urls && initial.image_urls.length
            ? initial.image_urls
            : initial.image_url ? [initial.image_url] : []
        ).slice(0, 3);
        while (arr.length < 3) arr.push("");
        return arr;
    };
    const [f, setF] = useState(initial);
    const [slots, setSlots] = useState(init3());

    const setSlot = (i, value) => {
        const next = [...slots];
        next[i] = value;
        setSlots(next);
    };

    const uploadSlot = (i) => (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 4 * 1024 * 1024) {
            toast.error("Please upload an image under 4MB");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setSlot(i, reader.result);
        reader.readAsDataURL(file);
    };

    const handleSave = () => {
        onSave({ ...f, image_urls: slots.filter(Boolean).slice(0, 3) });
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="service-editor">
            <div className="w-full max-w-3xl border border-zinc-800 bg-[#0a0a0a] p-8 max-h-[92vh] overflow-y-auto">
                <div className="font-display text-3xl mb-6">
                    {f.__new ? "New Service" : "Edit Service"}
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <Input label="TITLE" value={f.title} onChange={(v) => setF({ ...f, title: v })} />
                    <Input label="ICON (lucide)" value={f.icon} onChange={(v) => setF({ ...f, icon: v })} placeholder="e.g. car, sun, hammer" />
                    <div className="col-span-2">
                        <Input label="TAGLINE" value={f.tagline} onChange={(v) => setF({ ...f, tagline: v })} />
                    </div>
                    <div className="col-span-2">
                        <TextArea label="DESCRIPTION" value={f.description} onChange={(v) => setF({ ...f, description: v })} />
                    </div>
                    <Input label="ORDER" type="number" value={f.order} onChange={(v) => setF({ ...f, order: parseInt(v || "0", 10) })} />
                </div>

                {/* 3-photo grid */}
                <div className="mt-6">
                    <div className="font-mono-accent text-[10px] text-zinc-500 mb-3">
                        SERVICE PHOTOS · UP TO 3 (first photo shows first on the site)
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                        {slots.map((val, i) => (
                            <div key={i} className="border border-zinc-800 p-3 space-y-2">
                                <div className="font-mono-accent text-[9px] text-yellow-400">
                                    PHOTO {i + 1}
                                </div>
                                <div className="aspect-video bg-zinc-950 border border-zinc-900 relative">
                                    {val ? (
                                        <img src={val} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center font-mono-accent text-[9px] text-zinc-600">
                                            EMPTY
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <label className="flex-1 border border-zinc-700 hover:border-yellow-400 hover:text-yellow-400 px-2 py-2 font-mono-accent text-[9px] cursor-pointer inline-flex items-center justify-center gap-1">
                                        <Upload className="w-3 h-3" /> UPLOAD
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={uploadSlot(i)}
                                            className="hidden"
                                            data-testid={`service-image-upload-${i}`}
                                        />
                                    </label>
                                    {val && (
                                        <button
                                            type="button"
                                            onClick={() => setSlot(i, "")}
                                            className="border border-zinc-800 hover:border-red-500 hover:text-red-500 px-2 py-2 font-mono-accent text-[9px]"
                                        >
                                            CLEAR
                                        </button>
                                    )}
                                </div>
                                <input
                                    type="url"
                                    value={val && !val.startsWith("data:") ? val : ""}
                                    onChange={(e) => setSlot(i, e.target.value)}
                                    placeholder="or paste image URL"
                                    className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none text-white px-2 py-2 text-[10px]"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onCancel} className="border border-zinc-800 px-4 py-2 font-mono-accent text-[10px] text-zinc-400 hover:text-white">
                        CANCEL
                    </button>
                    <button
                        onClick={handleSave}
                        data-testid="save-service"
                        className="bg-yellow-400 text-black font-black uppercase tracking-wider px-6 py-3 text-xs hover:bg-yellow-300"
                    >
                        SAVE
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------- GALLERY ----------------
function GalleryPanel({ items, onChange }) {
    const [editing, setEditing] = useState(null);
    const [uploading, setUploading] = useState(false);

    const uploadNew = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 4 * 1024 * 1024) {
            toast.error("Please upload an image under 4MB");
            return;
        }
        setUploading(true);
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                await api.post("/gallery", {
                    title: f.name.replace(/\.[^.]+$/, ""),
                    category: "Project",
                    image_url: reader.result,
                    order: items.length + 1,
                });
                await onChange();
                toast.success("Image added");
            } catch {
                toast.error("Upload failed");
            } finally {
                setUploading(false);
            }
        };
        reader.readAsDataURL(f);
    };

    const del = async (id) => {
        if (!window.confirm("Delete this image?")) return;
        await api.delete(`/gallery/${id}`);
        await onChange();
        toast.success("Deleted");
    };

    const save = async (form) => {
        const { id, ...body } = form;
        await api.put(`/gallery/${id}`, body);
        await onChange();
        setEditing(null);
        toast.success("Updated");
    };

    return (
        <div data-testid="gallery-panel">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="font-display text-3xl">
                    Gallery <span className="text-yellow-400">({items.length})</span>
                </div>
                <label className="bg-yellow-400 text-black font-black uppercase tracking-wider px-4 py-3 text-xs hover:bg-yellow-300 cursor-pointer inline-flex items-center gap-2">
                    <Upload className="w-4 h-4" /> {uploading ? "UPLOADING…" : "UPLOAD IMAGE"}
                    <input
                        type="file"
                        accept="image/*"
                        onChange={uploadNew}
                        className="hidden"
                        data-testid="gallery-upload"
                        disabled={uploading}
                    />
                </label>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {items.map((g) => (
                    <div key={g.id} className="border border-zinc-900 bg-[#0a0a0a] group relative">
                        <img src={g.image_url} alt={g.title} className="w-full h-48 object-cover" />
                        <div className="p-3">
                            <div className="font-mono-accent text-[9px] text-yellow-400">{g.category}</div>
                            <div className="text-white text-sm truncate">{g.title}</div>
                        </div>
                        <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                            <button
                                onClick={() => setEditing(g)}
                                className="border border-zinc-700 hover:border-yellow-400 hover:text-yellow-400 p-2"
                                data-testid={`edit-gallery-${g.id}`}
                            >
                                <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => del(g.id)}
                                className="border border-zinc-700 hover:border-red-500 hover:text-red-500 p-2"
                                data-testid={`delete-gallery-${g.id}`}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <GalleryEditor
                    item={editing}
                    onCancel={() => setEditing(null)}
                    onSave={save}
                />
            )}
        </div>
    );
}

function GalleryEditor({ item, onCancel, onSave }) {
    const [f, setF] = useState(item);
    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="gallery-editor">
            <div className="w-full max-w-lg border border-zinc-800 bg-[#0a0a0a] p-8 max-h-[90vh] overflow-y-auto">
                <div className="font-display text-3xl mb-6">Edit Image</div>
                <img src={f.image_url} alt="" className="w-full h-40 object-cover mb-4 border border-zinc-800" />
                <div className="space-y-4">
                    <Input label="TITLE" value={f.title} onChange={(v) => setF({ ...f, title: v })} />
                    <Input label="CATEGORY" value={f.category} onChange={(v) => setF({ ...f, category: v })} />
                    <Input label="ORDER" type="number" value={f.order} onChange={(v) => setF({ ...f, order: parseInt(v || "0", 10) })} />
                </div>
                <div className="mt-6 flex justify-end gap-3">
                    <button onClick={onCancel} className="border border-zinc-800 px-4 py-2 font-mono-accent text-[10px] text-zinc-400 hover:text-white">
                        CANCEL
                    </button>
                    <button
                        onClick={() => onSave(f)}
                        className="bg-yellow-400 text-black font-black uppercase tracking-wider px-6 py-3 text-xs hover:bg-yellow-300"
                    >
                        SAVE
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------- TESTIMONIALS ----------------
const emptyT = { name: "", location: "Colorado Springs, CO", quote: "", rating: 5, order: 0 };

function TestimonialsPanel({ items, onChange }) {
    const [editing, setEditing] = useState(null);

    const save = async (form) => {
        try {
            if (form.__new) {
                const { __new, id, ...body } = form;
                await api.post("/testimonials", body);
                toast.success("Review added");
            } else {
                const { id, ...body } = form;
                await api.put(`/testimonials/${id}`, body);
                toast.success("Review updated");
            }
            setEditing(null);
            await onChange();
        } catch {
            toast.error("Save failed");
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this review?")) return;
        await api.delete(`/testimonials/${id}`);
        await onChange();
        toast.success("Deleted");
    };

    return (
        <div data-testid="testimonials-panel">
            <div className="flex items-center justify-between mb-6">
                <div className="font-display text-3xl">
                    Reviews <span className="text-yellow-400">({items.length})</span>
                </div>
                <button
                    onClick={() => setEditing({ ...emptyT, __new: true })}
                    data-testid="add-testimonial"
                    className="bg-yellow-400 text-black font-black uppercase tracking-wider px-4 py-3 text-xs hover:bg-yellow-300 inline-flex items-center gap-2"
                >
                    <Plus className="w-4 h-4" /> ADD REVIEW
                </button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {items.map((t) => (
                    <div key={t.id} className="border border-zinc-900 bg-[#0a0a0a] p-6">
                        <div className="flex justify-between items-start">
                            <div>
                                <div className="font-display text-2xl text-white">{t.name}</div>
                                <div className="font-mono-accent text-[10px] text-zinc-500">
                                    {t.location} · {t.rating}★
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setEditing(t)}
                                    className="border border-zinc-700 hover:border-yellow-400 p-2"
                                    data-testid={`edit-testimonial-${t.id}`}
                                >
                                    <Edit3 className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => del(t.id)}
                                    className="border border-zinc-800 hover:border-red-500 p-2"
                                    data-testid={`delete-testimonial-${t.id}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <p className="mt-4 text-zinc-300 text-sm italic">“{t.quote}”</p>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="testimonial-editor">
                    <div className="w-full max-w-2xl border border-zinc-800 bg-[#0a0a0a] p-8 max-h-[90vh] overflow-y-auto">
                        <div className="font-display text-3xl mb-6">
                            {editing.__new ? "New Review" : "Edit Review"}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Input label="NAME" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
                            <Input label="LOCATION" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} />
                            <Input label="RATING (1-5)" type="number" value={editing.rating} onChange={(v) => setEditing({ ...editing, rating: parseInt(v || "5", 10) })} />
                            <Input label="ORDER" type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: parseInt(v || "0", 10) })} />
                            <div className="col-span-2">
                                <TextArea label="QUOTE" value={editing.quote} onChange={(v) => setEditing({ ...editing, quote: v })} />
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <button onClick={() => setEditing(null)} className="border border-zinc-800 px-4 py-2 font-mono-accent text-[10px] text-zinc-400 hover:text-white">
                                CANCEL
                            </button>
                            <button
                                onClick={() => save(editing)}
                                data-testid="save-testimonial"
                                className="bg-yellow-400 text-black font-black uppercase tracking-wider px-6 py-3 text-xs hover:bg-yellow-300"
                            >
                                SAVE
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------- Reusable inputs ----------------
function Input({ label, value, onChange, type = "text", ...rest }) {
    return (
        <div>
            <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">{label}</label>
            <input
                type={type}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none text-white px-4 py-3"
                {...rest}
            />
        </div>
    );
}

function TextArea({ label, value, onChange }) {
    return (
        <div>
            <label className="font-mono-accent text-[10px] text-zinc-500 block mb-2">{label}</label>
            <textarea
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                rows={4}
                className="w-full bg-black border border-zinc-800 focus:border-yellow-400 outline-none text-white px-4 py-3 resize-none"
            />
        </div>
    );
}
